import { copyText } from "../lib/copy-text";

/* Copy buttons for code blocks in blog posts.
 *
 * Every engineering-blog competitor (Vercel, Tailwind, 11ty-based studio
 * blogs) lets a reader copy a snippet in one click; here the three posts
 * that ship fenced code (the OB-1 launch note, the MTP graft, the support
 * agent cost breakdown) force select-by-hand, and a long single-line
 * command that scrolls out of the block is effectively uncopiable on a
 * phone.
 *
 * This is a progressive enhancement, not a React component: gatsby-transformer-remark
 * gives us a static HTML string, so we decorate the rendered <pre> elements
 * after hydration. The copy payload is the code element's textContent — the
 * exact bytes the author wrote, with no button label or line numbers mixed
 * in (there is no highlighter, so there are no decorations to strip).
 *
 * Clipboard strategy per production write-ups on this exact component:
 *   1. navigator.clipboard.writeText (needs a secure context + permission)
 *   2. document.execCommand("copy") on an offscreen textarea if the API
 *      is unavailable or rejects the request
 *   3. neither worked: show "Failed" and announce "select and copy it
 *      manually" — never claim success.
 *
 * The button lives in a position:relative wrapper we build around the <pre>,
 * because the <pre> itself scrolls horizontally and an absolutely positioned
 * child would slide away with the content. The wrapper reserves no extra
 * space, so there is no layout shift when the enhancement runs.
 */

const COPY_LABEL = "Copy";
const COPIED_LABEL = "Copied";
/* Kept short so it fits in the reserved right padding; the live region
   carries the full instruction. */
const FAILED_LABEL = "Failed";

function decorate(pre) {
  /* Skip blocks we already decorated (route re-entries, React re-renders). */
  if (pre.dataset.codeCopy) return;
  pre.dataset.codeCopy = "1";

  const wrapper = document.createElement("div");
  wrapper.className = "code-block";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "code-block-copy";
  button.textContent = COPY_LABEL;
  /* Context for screen readers beyond the bare "Copy" (WCAG 2.4.6 and the
     copy-button a11y checklists: the label should say what is being copied). */
  button.setAttribute("aria-label", "Copy code to clipboard");

  const status = document.createElement("span");
  status.className = "code-block-status visually-hidden";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");

  let resetTimer;
  button.addEventListener("click", () => {
    const code = pre.querySelector("code") || pre;
    window.clearTimeout(resetTimer);
    copyText(code.textContent)
      .then(() => {
        button.textContent = COPIED_LABEL;
        button.classList.add("is-copied");
        status.textContent = COPIED_LABEL;
      })
      .catch(() => {
        button.textContent = FAILED_LABEL;
        status.textContent = "The copy failed. Select the code. Then copy it manually.";
      })
      .finally(() => {
        /* Restore the action label after the confirmation fades so the next
           reader sees a button, not a stale toast. */
        resetTimer = window.setTimeout(() => {
          button.textContent = COPY_LABEL;
          button.classList.remove("is-copied");
          status.textContent = "";
        }, 2500);
      });
  });

  pre.parentNode.insertBefore(wrapper, pre);
  wrapper.appendChild(button);
  wrapper.appendChild(status);
  wrapper.appendChild(pre);
}

/* Called from gatsby-browser on first load and after every client-side
   route change; scopes strictly to blog bodies so the rest of the site is
   untouched. */
export function enhanceCodeBlocks() {
  if (typeof document === "undefined") return;
  document.querySelectorAll(".blog-body pre").forEach(decorate);
}
